import { makeAutoObservable, runInAction } from "mobx";
import agent from "../api/agent";
import { ShopifyAuth } from "../models/ShopifyAuth";
import { User, UserFormValues } from "../models/User";
import { setShopifyAppHandle } from "../utils/shopifyAdmin";
import { store } from "./store";

export default class UserStore {
    user: User | null = null;    
    private authenticationRequest = 0;

    constructor() {
        makeAutoObservable(this)
    }
    
    get isLoggedIn() {        
        return !!this.user;
    }

    authenticate = async (creds: UserFormValues) => {  
        try {            
            // We're going to use Shopify to authenticate user
            // Get authentication URL for shop 
            const shopifyAuth = await agent.Account.auth(creds);
            window.location.replace(shopifyAuth); 
        } catch (error) {
            throw error;
        }
    }

    embeddedLogin = async (params: ShopifyAuth) => {  
        const request = ++this.authenticationRequest;
        try {            
            // Bootstrap the installation using a verified Shopify ID token.
            // Subsequent requests obtain their own fresh ID token from App Bridge.
            const user = await agent.Account.embeddedlogin(params);
            if (request !== this.authenticationRequest) return;
            setShopifyAppHandle(user.shopifyAppHandle);
            store.commonStore.useShopifySession();
            runInAction(() => this.user = user);        
        } catch (error) {
            throw error;
        }
    }

    login = async (params: ShopifyAuth) => {        
        const request = ++this.authenticationRequest;
        try {
            // Once user has been authenticated through Shopify's OAuth, we create a local session
            // See AccountController for details
            const user = await agent.Account.login(params);
            if (request !== this.authenticationRequest) return;
            setShopifyAppHandle(user.shopifyAppHandle);
            // We get the user object back, including a token, and set the token to memory
            store.commonStore.setToken(user.token);
            runInAction(() => this.user = user);                 
        } catch (error) {
            throw error;
        }        
    }
   
    logout = (recoverEmbeddedSession = false) => {
        this.authenticationRequest++;
        store.commonStore.invalidateSession(recoverEmbeddedSession);
        window.sessionStorage.removeItem('gv-pf-base-jwt');
        window.sessionStorage.removeItem('shopify-app-handle');
        this.user = null;
    }

    legacyPilotLogin = async (code: string, operator = false) => {
        // A handoff must never leave another shop's existing session active if
        // the ticket is invalid, expired, consumed or its route has changed.
        this.logout();
        const request = ++this.authenticationRequest;
        const user = await (operator ? agent.Account.redeemOperatorSession(code) : agent.Account.redeemLegacyPilot(code));
        if (request !== this.authenticationRequest) return;
        if (typeof user?.token !== 'string' || !user.token.trim()
            || typeof user.shopDomain !== 'string' || !user.shopDomain.trim()) throw new Error('Invalid pilot session response.');
        setShopifyAppHandle(user.shopifyAppHandle);
        store.commonStore.setToken(user.token);
        runInAction(() => this.user = user);
        store.commonStore.setAppLoaded();
    }
    
    getUser = async () => {        
        const request = ++this.authenticationRequest;
        try
        {   const user = await agent.Account.current();
            if (request !== this.authenticationRequest) return;

            setShopifyAppHandle(user.shopifyAppHandle);
            runInAction(() => this.user = user);
        } catch (error: any) {
            const status = error.response?.status;
            if (request === this.authenticationRequest && (status === 401 || status === 403 || status === 404)) {
                this.logout(true);
            }
        }
    }
}
